import {useMemo} from 'react';

import {Box} from '@mui/system';
import {useTranslation} from 'react-i18next';

import {AllowedCountryCodesSignIn} from '@/app/auth/constants/AllowedPhoneNumbers';
import {
  AppButton,
  ButtonContainer,
  PhoneInputTextField,
} from '@/app/shared-components';
import {AppButtonVariant} from '@/app/shared-components/AppButton/models/interfaces';
import PasswordTextField from '@/app/shared-components/PasswordTextField/PasswordTextField';
import {SeparateChildren} from '@/utils/SeparateChildren';

import {AuthenticationWrapper} from '../../components/AuthenticationWrapper';

import {
  SignInViewModelProvider,
  useSignInViewModelProvider,
} from './ViewModelContext';

import './styles';

const SignInContent = () => {
  const {t} = useTranslation();

  const {
    phone,
    password,
    setPhone,
    setPassword,
    isLoadingAmplifyAuth,
    handlePressSignIn,
    handlePressSignUp,
    handlePressForgotPassword,
  } = useSignInViewModelProvider();

  const isDisabledSignInButton = useMemo(
    () => !phone || !password || password?.length < 8,
    [phone, password],
  );

  return (
    <AuthenticationWrapper title={t('sign_in.title')}>
      <form style={{paddingInline: 16}}>
        <SeparateChildren Separator={() => <div style={{height: '16px'}} />}>
          <PhoneInputTextField
            id="SignInPage_phoneInput"
            value={phone}
            onChange={setPhone}
            countries={AllowedCountryCodesSignIn}
            showCountryCode={true}
          />
          <SeparateChildren Separator={() => <div style={{height: '5px'}} />}>
            <PasswordTextField
              id="SignInPage_passwordInput"
              label={t('sign_in.password')}
              password={password}
              setPassword={setPassword}
              autocomplete="current-password"
            />

            <Box className="forgot-password-container">
              <AppButton
                id="SignInPage_forgotPasswordButton"
                label={t('sign_in.forgot_password')}
                onClick={handlePressForgotPassword}
                variant={AppButtonVariant.onText}
                removeStyles={true}
              />
            </Box>
          </SeparateChildren>
        </SeparateChildren>
      </form>

      <ButtonContainer sticky>
        <Box sx={{marginTop: '16px'}}>
          <AppButton
            id="SignInPage_signInButton"
            onClick={handlePressSignIn}
            label={t('sign_in.button')}
            disabled={isDisabledSignInButton || isLoadingAmplifyAuth}
            isLoading={isLoadingAmplifyAuth}
          />
        </Box>

        <AppButton
          id="SignInPage_signUpButton"
          label={t('sign_in.sign_up_button')}
          variant={AppButtonVariant.onSecondary}
          onClick={handlePressSignUp}
        />
      </ButtonContainer>
    </AuthenticationWrapper>
  );
};

const SignInPage = () => (
  <SignInViewModelProvider>
    <SignInContent />
  </SignInViewModelProvider>
);

export default SignInPage;
